import { useCallback, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ExperienceCompactSearchTile from '../ExperienceTiles/ExperienceCompactSearchTile'
import ExperienceCompactSearchTileSkeleton from '../ExperienceTiles/ExperienceCompactSearchTileSkeleton'
import { fetchExperiencesById } from 'actions/ExperienceActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import { setContentTagAttribution } from 'lib/contentTags/contentTagAttribution'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ExperienceSearchPageSort from './ExperienceSearchPageSort'
import useExperiencesSortOptions from '../hooks/useExperiencesSortOptions'

const ListGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(${rem(300)}, 1fr));
    gap: ${rem(36)} ${rem(16)};
  }
`

const ContentTagResultsContent = styled(VerticalSpacer)`
  margin-top: ${rem(40)};

  ${mediaQueryDown.mobile} {
    margin-top: ${rem(8)};
  }
`

interface Props {
  offerIds: Array<string>
  fetching: boolean
  listFetching?: boolean
  error?: string
  onClear: () => void
  contentTagId?: string
}

function ExperienceSearchContentTagResults({ offerIds, fetching, listFetching, error, onClear, contentTagId }: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { sortBy } = useExperiencesSortOptions()
  const experiences = useAppSelector(state => state.experience.experiences)
  const experienceErrors = useAppSelector(state => state.experience.experienceErrors)

  useEffect(() => {
    if (offerIds.length) dispatch(fetchExperiencesById(...offerIds))
  }, [dispatch, offerIds])

  const loaded = useMemo(() => offerIds.map(id => experiences[id]).filter(Boolean), [experiences, offerIds])
  const sortedLoaded = useMemo(() => {
    switch (sortBy) {
      case 'price.asc': return [...loaded].sort((a, b) => a.price - b.price)
      case 'price.desc': return [...loaded].sort((a, b) => b.price - a.price)
      case 'customerRating': return [...loaded].sort((a, b) => (b.rating?.score ?? 0) - (a.rating?.score ?? 0))
      case 'bestValue': return [...loaded].sort((a, b) => b.discount - a.discount)
      default: return loaded
    }
  }, [loaded, sortBy])
  const resolvedCount = useMemo(
    () => offerIds.filter(id => experiences[id] || experienceErrors[id]).length,
    [experienceErrors, experiences, offerIds],
  )
  const isLoading = fetching || listFetching || (offerIds.length > 0 && resolvedCount !== offerIds.length)
  const handleOfferClick = useCallback(() => {
    if (contentTagId) setContentTagAttribution(contentTagId)
  }, [contentTagId])
  const clearLabel = intl.formatMessage({
    id: 'lAeBIFQBqli6ThP7-r-j',
    defaultMessage: 'Clear selected category',
    description: 'Action that clears a selected category filter after no matching experiences were found',
  })

  if (error) {
    return <VerticalSpacer gap={8}>
      <BodyText variant="large">{intl.formatMessage({
        id: 'bxL2P8VoSh0DD18p5XUl',
        defaultMessage: 'We could not load this category. Please try again.',
        description: 'Error message displayed when a selected category result request fails',
      })}
      </BodyText>
      <div><TextButton kind="tertiary" size="small" onClick={onClear}>{clearLabel}</TextButton></div>
    </VerticalSpacer>
  }

  if (!isLoading && offerIds.length === 0) {
    return <VerticalSpacer gap={8}>
      <BodyText variant="large">{intl.formatMessage({
        id: 'mFAO-LSkLLTkS_GF0num',
        defaultMessage: 'No experiences match this category with your current filters.',
        description: 'No-results message for a selected category on experiences search',
      })}
      </BodyText>
      <div><TextButton kind="tertiary" size="small" onClick={onClear}>{clearLabel}</TextButton></div>
    </VerticalSpacer>
  }

  return <ContentTagResultsContent gap={16}>
    <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
      {offerIds.length > 0 && <BodyText variant="medium" colour="neutral-two">
        {intl.formatMessage(
          {
            id: 'tXFmCPud-a05Cl708Hm6',
            defaultMessage: '{count, plural, one {# experience} other {# experiences found}}',
            description: 'Pluralized label showing the number of experiences returned in search results',
          },
          { count: offerIds.length },
        )}
      </BodyText>}
      <CSSBreakpoint min="tablet">
        <ExperienceSearchPageSort />
      </CSSBreakpoint>
    </Group>
    <ListGrid>
      {isLoading && Array.from({ length: 4 }, (_, index) => <ExperienceCompactSearchTileSkeleton key={index} />)}
      {!isLoading && sortedLoaded.map((experience, index) => <ExperienceCompactSearchTile
        key={experience.id}
        experience={experience}
        position={index}
        onOfferClick={contentTagId ? handleOfferClick : undefined}
      />)}
    </ListGrid>
  </ContentTagResultsContent>
}

export default ExperienceSearchContentTagResults
